import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import { fetchProductCategories, createCategory, deleteCategory } from '../api/productApi';
import { useThemeContext } from '../context/ThemeContext';
import { updateCategory as updateCategoryApi } from '../api/productApi';
import EditIcon from '@mui/icons-material/Edit';



const CategoriesPage = () => {
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
const [categoryToEdit, setCategoryToEdit] = useState<{ id: number; name: string } | null>(null);
const [editName, setEditName] = useState('');

  const { enqueueSnackbar } = useSnackbar();
  const { isDarkMode } = useThemeContext();
  const theme = useTheme();

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const fetchedCategories = await fetchProductCategories();
        setCategories(fetchedCategories);
      } catch (error) {
        enqueueSnackbar('Failed to load categories', { variant: 'error' });
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, [enqueueSnackbar]);

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const newCategory = await createCategory({ name: newCategoryName.trim() });
      setCategories([...categories, newCategory]);
      setNewCategoryDialogOpen(false);
      setNewCategoryName('');
      enqueueSnackbar('Category created successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to create category', { variant: 'error' });
      console.error('Error creating category:', error);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    try {
      await deleteCategory(categoryToDelete);
      setCategories(categories.filter(c => c.id !== categoryToDelete));
      setDeleteDialogOpen(false);
      enqueueSnackbar('Category deleted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to delete category', { variant: 'error' });
      console.error('Error deleting category:', error);
    }
  };

  const openEdit = (cat: { id: number; name: string }) => {
  setCategoryToEdit(cat);
  setEditName(cat.name);
  setEditDialogOpen(true);
};

const handleUpdateCategory = async () => {
  if (!categoryToEdit || !editName.trim()) return;
  try {
    const updated = await updateCategoryApi(categoryToEdit.id, { name: editName.trim() });
    setCategories(prev => prev.map(c => (c.id === updated.id ? { ...c, name: updated.name } : c)));
    setEditDialogOpen(false);
    enqueueSnackbar('Category updated', { variant: 'success' });
  } catch (err: any) {
    const msg = err?.response?.data?.error || 'Failed to update category';
    enqueueSnackbar(msg, { variant: 'error' });
  }
};


  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Product Categories</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewCategoryDialogOpen(true)}
        >
          Add Category
        </Button>
      </Box>

      <Card>
        <CardContent>
          <List>
            {categories.map((category) => (
              <ListItem
                key={category.id}
                secondaryAction={
  <Box>
    <IconButton edge="end" aria-label="edit" onClick={() => openEdit(category)} sx={{ mr: 1 }}>
      <EditIcon />
    </IconButton>
    {category.id !== 1 && (
      <IconButton
        edge="end"
        aria-label="delete"
        onClick={() => {
          setCategoryToDelete(category.id);
          setDeleteDialogOpen(true);
        }}
        color="error"
      >
        <DeleteIcon />
      </IconButton>
    )}
  </Box>
}

              >
                <ListItemText primary={category.name} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* New Category Dialog */}
      <Dialog open={newCategoryDialogOpen} onClose={() => setNewCategoryDialogOpen(false)}>
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCategoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCategory} disabled={!newCategoryName}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
  <DialogTitle>Edit Category</DialogTitle>
  <DialogContent>
    <TextField
      autoFocus
      margin="dense"
      label="Category Name"
      fullWidth
      value={editName}
      onChange={(e) => setEditName(e.target.value)}
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
    <Button onClick={handleUpdateCategory} disabled={!editName.trim()} variant="contained">
      Save
    </Button>
  </DialogActions>
</Dialog>


      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this category? Products in this category will need to be reassigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCategory} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesPage;